import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import FeaturedEventsData from './FeaturedEventsData';
import './FeaturedEvents.css';

const AUTOPLAY_MS = 7000;
const DONATE_URL = 'https://donationusa.yugalkunj.org/rms/';

// Format date for display (e.g., "October 14, 2026" or "Oct 14-20, 2026")
const formatDateDisplay = (i_startDate, i_endDate) => {
  const o_start = new Date(i_startDate);
  const o_end = new Date(i_endDate);
  
  const o_options = { month: 'short', day: 'numeric', year: 'numeric' };
  
  if (i_startDate === i_endDate) {
    return o_start.toLocaleDateString('en-US', o_options);
  }
  
  if (o_start.getMonth() === o_end.getMonth() && o_start.getFullYear() === o_end.getFullYear()) {
    return `${o_start.toLocaleDateString('en-US', { month: 'short' })} ${o_start.getDate()}-${o_end.getDate()}, ${o_start.getFullYear()}`;
  }
  
  return `${o_start.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${o_end.toLocaleDateString('en-US', o_options)}`;
};

// Generate Google Calendar URL
const createGoogleCalendarUrl = (i_event) => {
  const o_baseUrl = 'https://calendar.google.com/calendar/render?action=TEMPLATE';
  const o_title = encodeURIComponent(i_event.title);
  const o_details = encodeURIComponent(i_event.desc);
  const o_location = encodeURIComponent(i_event.location);
  
  // Parse start date and time
  const o_startDate = new Date(i_event.startDate);
  const o_endDate = new Date(i_event.endDate);
  
  // For all-day events or events without specific times
  const o_dates = `${i_event.startDate.replace(/-/g, '')}/${i_event.endDate.replace(/-/g, '')}`;
  
  return `${o_baseUrl}&text=${o_title}&dates=${o_dates}&details=${o_details}&location=${o_location}`;
};

// Homepage flyer carousel: one featured event per slide with its details and flyer.
const FeaturedEvents = () => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  // Only upcoming flyers, soonest first (same rule as the Services section)
  const events = FeaturedEventsData
    .filter((i_event) => !i_event.endDate || new Date(i_event.endDate) >= today)
    .sort((a, b) => new Date(a.startDate || '2100-01-01') - new Date(b.startDate || '2100-01-01'));

  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);
  const total = events.length;

  const goTo = useCallback((i_index) => setActive(((i_index % total) + total) % total), [total]);

  useEffect(() => {
    if (paused || total < 2) return undefined;
    const o_timer = setInterval(() => setActive((i_prev) => (i_prev + 1) % total), AUTOPLAY_MS);
    return () => clearInterval(o_timer);
  }, [paused, total]);

  if (total === 0) return null;
  const event = events[active];
  const pad = (i_n) => String(i_n + 1).padStart(2, '0');

  return (
    <section
      className="yk-featured"
      aria-roledescription="carousel"
      aria-label="Featured events"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)}
      onBlur={() => setPaused(false)}
    >
      <div className="yk-featured-inner">
        <div className="yk-featured-slide" key={event.id} aria-live="polite">
          <div className="yk-featured-copy">
            <span className="yk-featured-tag">{event.tag}</span>
            <h2 className="yk-featured-title">{event.title}</h2>
            <p className="yk-featured-desc">{event.desc}</p>

            <div className="yk-featured-info-grid">
              <button
                type="button"
                className="yk-featured-info-card yk-info-date"
                onClick={() => window.open(createGoogleCalendarUrl(event), '_blank')}
                aria-label="Add to Google Calendar"
              >
                <i className="fa fa-calendar-alt" aria-hidden="true"></i>
                <div>
                  <span className="yk-info-label">Date</span>
                  <span className="yk-info-value">{formatDateDisplay(event.startDate, event.endDate)}</span>
                </div>
              </button>

              <div className="yk-featured-info-card yk-info-time">
                <i className="fa fa-clock" aria-hidden="true"></i>
                <div>
                  <span className="yk-info-label">Time</span>
                  <span className="yk-info-value">{event.time}</span>
                </div>
              </div>

              <div className="yk-featured-info-card yk-info-location">
                <i className="fa fa-map-marker-alt" aria-hidden="true"></i>
                <div>
                  <span className="yk-info-label">Location</span>
                  <span className="yk-info-value">{event.location}</span>
                </div>
              </div>
            </div>

            {event.highlights && event.highlights.length > 0 && (
              <div className="yk-featured-highlights">
                <h4 className="yk-highlights-title">Event Highlights</h4>
                <ul className="yk-highlights-list">
                  {event.highlights.map((highlight, index) => (
                    <li key={index}>
                      <i className="fa fa-check-circle" aria-hidden="true"></i>
                      {highlight}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {event.badge && <span className="yk-featured-badge">{event.badge}</span>}

            <div className="yk-featured-actions">
              {event.registrationUrl ? (
                <a
                  href={event.registrationUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="yk-featured-btn yk-btn-primary"
                >
                  <i className="fa fa-user-plus" aria-hidden="true"></i>
                  Register Now
                </a>
              ) : (
                <Link to={event.link} className="yk-featured-btn yk-btn-primary">
                  Learn More <span aria-hidden="true">→</span>
                </Link>
              )}
              <a href={DONATE_URL} target="_blank" rel="noreferrer" className="yk-featured-btn yk-btn-secondary">
                <i className="fa fa-heart" aria-hidden="true"></i> Donate
              </a>
            </div>
          </div>

          <div className="yk-featured-flyer">
            <a href={event.image} target="_blank" rel="noreferrer" aria-label={`Open ${event.title} flyer`}>
              <img src={event.image} alt={`${event.title} flyer`} loading={active === 0 ? 'eager' : 'lazy'} />
            </a>
          </div>
        </div>

        {total > 1 && (
          <div className="yk-featured-controls">
            <div className="yk-featured-dots" role="tablist" aria-label="Choose event">
              {events.map((i_event, i_index) => (
                <button
                  key={i_event.id}
                  type="button"
                  role="tab"
                  aria-selected={i_index === active}
                  aria-label={`${i_index + 1}: ${i_event.title}`}
                  className={i_index === active ? 'is-active' : ''}
                  onClick={() => goTo(i_index)}
                />
              ))}
              <span className="yk-featured-count">{pad(active)} / {pad(total - 1)}</span>
            </div>
            <div className="yk-featured-arrows">
              <button type="button" onClick={() => goTo(active - 1)} aria-label="Previous event">
                <i className="fa fa-chevron-left" aria-hidden="true"></i>
              </button>
              <button type="button" onClick={() => goTo(active + 1)} aria-label="Next event">
                <i className="fa fa-chevron-right" aria-hidden="true"></i>
              </button>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default FeaturedEvents;
